import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Modal, Box, Typography, Button } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CONST from "../constants";

function AutoLogout() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const showWarningRef = useRef(false);

  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(60);

  const isAuthenticated = localStorage.getItem("UserAuthenticated");

  const clearAllTimers = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }
  };

  const logout = () => {
    localStorage.removeItem("UserAuthenticated");
    localStorage.removeItem(CONST.ENROLLMENT_MODAL_SHOWN_KEY);
    setShowWarning(false);
    showWarningRef.current = false;
    clearAllTimers();
    navigate("/", { replace: true });
  };

  const startCountdown = () => {
    setCountdown(60);
    countdownIntervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownIntervalRef.current);
          logout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const showWarningModal = () => {
    setShowWarning(true);
    showWarningRef.current = true;
    startCountdown();
  };

  const resetTimer = () => {
    clearAllTimers();
    setShowWarning(false);
    showWarningRef.current = false;

    if (isAuthenticated) {
      // Set warning to show 1 minute before logout (60 seconds before 120 seconds)
      warningTimeoutRef.current = setTimeout(showWarningModal, 60000);
      // Set actual logout for 2 minutes
      timeoutRef.current = setTimeout(logout, 120000);
    }
  };

  const stayLoggedIn = () => {
    setShowWarning(false);
    showWarningRef.current = false;
    resetTimer();
  };

  useEffect(() => {
    const handleActivity = () => {
      // Only reset timer if warning modal is not currently shown
      if (!showWarningRef.current) {
        resetTimer();
      }
    };

    window.addEventListener("mousemove", handleActivity);
    window.addEventListener("keydown", handleActivity);
    window.addEventListener("click", handleActivity);

    // Only call resetTimer on initial mount or when authentication status changes
    if (!showWarningRef.current) {
      resetTimer();
    }

    return () => {
      clearAllTimers();
      window.removeEventListener("mousemove", handleActivity);
      window.removeEventListener("keydown", handleActivity);
      window.removeEventListener("click", handleActivity);
    };
  }, [isAuthenticated]);

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: window.innerWidth <= 768 ? "80%" : 600,
    maxWidth: window.innerWidth <= 768 ? "none" : 600,
    bgcolor: "background.paper",
    borderRadius: 2,
    boxShadow: 24,
    p: 4,
    textAlign: "center",
  };

  return (
    <Modal
      open={showWarning}
      disableEscapeKeyDown
      onClose={() => {}} // Prevent closing on backdrop click
      aria-labelledby="auto-logout-warning-title"
      aria-describedby="auto-logout-warning-description"
      slotProps={{
        backdrop: {
          onClick: (event) => {
            event.stopPropagation(); // Prevent backdrop click from closing modal
          },
        },
      }}
    >
      <Box sx={modalStyle}>
        <Typography
          id="auto-logout-warning-title"
          variant="h6"
          component="h2"
          sx={{
            mb: 1,
            fontFamily: "Inter",
            fontWeight: "bold",
            textAlign: "left",
          }}
        >
          {t("autoLogout.warningTitle")}
        </Typography>

        <Typography
          id="auto-logout-warning-description"
          sx={{
            mb: 4,
            fontFamily: "Inter",
            color: "text.secondary",
            textAlign: "left",
            fontSize: "16px",
          }}
        >
          {t("autoLogout.warningMessage", { seconds: countdown })}
        </Typography>
        <Box
          style={{
            borderRadius: "8px",
            border: "1px solid #B39003",
            background: "#FFFBEB",
            padding: "16px",
            marginBottom: "24px",
          }}
        >
          <Box sx={{ mb: 3, textAlign: "center" }}>
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontFamily: "Inter",
                fontWeight: "600",
                color: "#92400D",
                fontSize: "16px",
              }}
            >
              {t("autoLogout.timeRemaining")}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
              }}
            >
              <AccessTimeIcon
                sx={{
                  color: "#92400D",
                  fontSize: 24,
                }}
              />
              <Typography
                variant="h4"
                component="div"
                sx={{
                  fontWeight: "bold",
                  color: "#92400D",
                  fontSize: "24px",
                  fontFamily: "Inter",
                }}
              >
                {Math.floor(countdown / 60)}:
                {(countdown % 60).toString().padStart(2, "0")}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            justifyContent: "center",
          }}
        >
          <Button
            onClick={stayLoggedIn}
            variant="contained"
            sx={{
              backgroundColor: "#216BB1",
              "&:hover": { backgroundColor: "#0051D5" },
              fontFamily: "Inter",
              textTransform: "capitalize",
              px: 3,
              py: 1,
              borderRadius: "8px",
              width: "100%",
              color: "#fff",
            }}
          >
            {t("autoLogout.stayLoggedIn")}
          </Button>

          <Button
            onClick={logout}
            variant="outlined"
            sx={{
              borderColor: "#303030",
              color: "#303030",
              "&:hover": {
                borderColor: "#303030",
              },
              fontFamily: "Inter",
              textTransform: "capitalize",
              px: 3,
              py: 1,
              width: "100%",
              borderRadius: "8px",
            }}
          >
            {t("autoLogout.logOut")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}

export default AutoLogout;
